import axios from "axios"

import EnvVars from "~/env-vars"

import axiosInstance from "../api.axiosInstance"
import type {
  BatteryPresentationShareStateDto,
  BatteryPresentationViewsDto,
  PublicBatteryPresentationDto,
} from "../dto/battery-presentation.dto"
import { withReadableErrorBody } from "../readable-error-body"

const managementPath = (proposalId: string): string =>
  `/battery-proposal/v2/${proposalId}/presentation-link`

const exportPath = (proposalId: string): string =>
  `/battery-proposal/v2/${proposalId}/presentation-export`

/**
 * Recipient requests carry no Vision JWT. `axiosInstance` attaches one from
 * localStorage, so a logged-in seller opening a shared link would silently
 * read it as an authenticated caller — these use a bare client instead.
 */
const publicUrl = (path: string): string => `${EnvVars.API_URL}${path}`

/** Bare axios has no timeout of its own; a hung gateway must not leave the
 * recipient on the loading text forever. Matches `axiosInstance`. */
const PUBLIC_READ_TIMEOUT_MS = 30_000

/**
 * `axiosInstance`'s own 30s ceiling is far below what a brochure render costs:
 * the gateway gives Chromium 120s, so a client that gave up at 30s would call
 * every ordinary export a failure while the render it abandoned went on to
 * succeed. This sits past the server's budget so the server's answer wins.
 */
const EXPORT_TIMEOUT_MS = 150_000

export class BatteryPresentationService {
  async getShareState(
    proposalId: string,
  ): Promise<BatteryPresentationShareStateDto> {
    const response = await axiosInstance.get<BatteryPresentationShareStateDto>(
      managementPath(proposalId),
    )

    return response.data
  }

  /** Publishing is bodyless; the server owns readiness and the token. */
  async publish(proposalId: string): Promise<BatteryPresentationShareStateDto> {
    const response = await axiosInstance.post<BatteryPresentationShareStateDto>(
      managementPath(proposalId),
    )

    return response.data
  }

  async unpublish(
    proposalId: string,
  ): Promise<BatteryPresentationShareStateDto> {
    const response =
      await axiosInstance.delete<BatteryPresentationShareStateDto>(
        managementPath(proposalId),
      )

    return response.data
  }

  async getViews(proposalId: string): Promise<BatteryPresentationViewsDto> {
    const response = await axiosInstance.get<BatteryPresentationViewsDto>(
      `${managementPath(proposalId)}/views`,
    )

    return response.data
  }

  /** The generated brochure PDF. The gateway renders it on demand and keeps
   * no copy, so there is nothing to poll for and nothing to cache. */
  async exportBrochure(proposalId: string): Promise<Blob> {
    try {
      // No body, not even `null`: axios would serialize that to the literal
      // "null" with a JSON content type, and the gateway's body parser rejects
      // it with a 400 whose response carries no CORS header, so the browser
      // reports a CORS failure for what is really a malformed request.
      const response = await axiosInstance.post<ArrayBuffer>(
        exportPath(proposalId),
        undefined,
        { responseType: `arraybuffer`, timeout: EXPORT_TIMEOUT_MS },
      )

      return new Blob([response.data], { type: `application/pdf` })
    } catch (error) {
      throw await withReadableErrorBody(error)
    }
  }

  /**
   * The presentation for the print page Chromium is rendering. Like the public
   * read, it carries no Vision JWT: the print token is the whole authority.
   */
  async readForExport(token: string): Promise<PublicBatteryPresentationDto> {
    const response = await axios.get<PublicBatteryPresentationDto>(
      publicUrl(
        `/battery-proposal/v2/presentation-exports/${encodeURIComponent(token)}`,
      ),
      { timeout: PUBLIC_READ_TIMEOUT_MS },
    )

    return response.data
  }

  async readPublic(token: string): Promise<PublicBatteryPresentationDto> {
    const response = await axios.get<PublicBatteryPresentationDto>(
      publicUrl(
        `/battery-proposal/v2/public-presentations/${encodeURIComponent(token)}`,
      ),
      { timeout: PUBLIC_READ_TIMEOUT_MS },
    )

    return response.data
  }
}

export const batteryPresentationService = new BatteryPresentationService()
